import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";

const float = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 0px rgba(99,102,241,0.0); }
  50% { box-shadow: 0 10px 35px rgba(99,102,241,0.20); }
  100% { box-shadow: 0 0 0px rgba(99,102,241,0.0); }
`;

const Wrapper = styled.div`
  min-height: 100dvh;
  display: grid;
  place-items: center;
  background: radial-gradient(1200px 600px at 20% -10%, #eef2ff 0%, transparent 60%),
              radial-gradient(900px 500px at 110% 10%, #fce7f3 0%, transparent 60%),
              linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  color: #0f172a;
  padding: 24px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 860px;
  background: rgba(255,255,255,0.75);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.10);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 28px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Big404 = styled.div`
  font-size: clamp(64px, 10vw, 140px);
  font-weight: 800;
  letter-spacing: -2px;
  line-height: 1;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 35%, #ec4899 90%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: ${float} 4s ease-in-out infinite;
`;

const Title = styled.h1`
  font-size: clamp(22px, 2.2vw, 28px);
  margin: 12px 0 10px;
`;

const Subtitle = styled.p`
  color: #475569;
  margin: 0 0 18px;
`;

const Panel = styled.div`
  display: grid;
  gap: 14px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;
`;

const Btn = styled.button`
  appearance: none;
  border: 0;
  border-radius: 14px;
  padding: 12px 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 150ms ease, box-shadow 150ms ease, background 150ms ease;
  background: ${(p) => (p.variant === "primary" ? "linear-gradient(135deg, #6366f1, #8b5cf6)" : "#f1f5f9")};
  color: ${(p) => (p.variant === "primary" ? "#fff" : "#0f172a")};
  animation: ${(p) => (p.variant === "primary" ? glow : "none")} 4s ease-in-out infinite;

  &:hover { transform: translateY(-1px); }
  &:active { transform: translateY(0); }
`;

const QuickLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Chip = styled.button`
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #0f172a;
  border-radius: 999px;
  padding: 8px 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 150ms ease, transform 150ms ease;
  &:hover { background: #f8fafc; transform: translateY(-1px); }
`;

const Box = styled.div`
  background: #0b1220;
  color: #e2e8f0;
  border-radius: 18px;
  padding: 22px;
  display: grid;
  gap: 14px;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const Angle = styled.div`
  position: absolute;
  inset: -1px;
  background: radial-gradient(600px 160px at -20% -20%, rgba(99,102,241,0.25) 0%, transparent 60%),
              radial-gradient(500px 220px at 120% -10%, rgba(236,72,153,0.25) 0%, transparent 60%);
  pointer-events: none;
`;

const MiniTitle = styled.div`
  font-weight: 700;
  opacity: 0.9;
`;

const Mono = styled.code`
  background: rgba(148,163,184,0.1);
  border: 1px dashed rgba(148,163,184,0.25);
  padding: 6px 8px;
  border-radius: 10px;
  font-size: 12px;
  color: #cbd5e1;
`;

const InputWrap = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Input = styled.input`
  flex: 1;
  background: #0f172a;
  color: #e5e7eb;
  border: 1px solid rgba(148,163,184,0.25);
  border-radius: 12px;
  padding: 12px 14px;
  outline: none;
  transition: border 150ms ease;
  &:focus { border-color: #6366f1; }
`;

const Illustration = styled.div`
  display: grid;
  place-items: center;
  background: conic-gradient(from 180deg at 50% 50%, #1e293b 0deg, #111827 360deg);
  border-radius: 18px;
  padding: 18px;
  position: relative;
  min-height: 240px;
  overflow: hidden;
`;

const Svg = styled.svg`
  width: 100%;
  max-width: 380px;
  filter: drop-shadow(0 24px 40px rgba(0,0,0,0.35));
`;

export default function NotFound() {
  const navigate = useNavigate();
  const location = useLocation();
  const [target, setTarget] = useState("");

  const go = (path) => navigate(path);
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate("/"));

  const quick = [
    { label: "Dla Partnerów", to: "/partner" },
    { label: "Cennik", to: "/cennik" },
    { label: "Kontakt", to: "/contact" },

  ];

  const onSubmit = (e) => {
    e.preventDefault();
    if (!target.trim()) return;
    // naive route jump; you can replace with your own site search
    navigate(target.startsWith("/") ? target : `/${target}`);
  };

  return (
    <Wrapper>
      <Card>
        <Grid>
          <Panel>
            <Big404>404</Big404>
            <Title>Ups! Tej strony nie ma…</Title>
            <Subtitle>
              Adres <Mono>{location.pathname}</Mono> nie istnieje lub został przeniesiony.
              Sprawdź pisownię albo skorzystaj z przycisków poniżej.
            </Subtitle>

            <Actions>
              <Btn onClick={goBack}>Wróć</Btn>
              <Btn variant="primary" onClick={() => go("/")}>Strona główna</Btn>
            </Actions>

            <div style={{ height: 8 }} />

            <MiniTitle>Szybkie przejścia</MiniTitle>
            <QuickLinks>
              {quick.map((q) => (
                <Chip key={q.to} onClick={() => go(q.to)}>{q.label}</Chip>
              ))}
            </QuickLinks>
          </Panel>

          <Box>
            <Angle />
            <MiniTitle>Przejdź do ścieżki</MiniTitle>
            <form onSubmit={onSubmit}>
              <InputWrap>
                <Input
                  placeholder="/sprzedaz/dashboard"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                />
                <Btn type="submit">Idź</Btn>
              </InputWrap>
            </form>

            <Illustration aria-hidden="true">
              <Svg viewBox="0 0 400 270">
                <defs>
                  <linearGradient id="grad" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" />
                    <stop offset="60%" stopColor="#8b5cf6" />
                    <stop offset="100%" stopColor="#ec4899" />
                  </linearGradient>
                </defs>
                {/* Simple abstract “map”/“route” illustration */}
                <rect x="18" y="18" width="364" height="234" rx="16" fill="#0f172a" />
                <g opacity="0.12" stroke="#ffffff">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <path key={i} d={`M${40+i*46} 40 L${80+i*40} 110 L${40+i*46} 190`} fill="none"/>
                  ))}
                </g>
                <circle cx="90" cy="70" r="8" fill="url(#grad)" />
                <circle cx="270" cy="150" r="8" fill="url(#grad)" />
                <circle cx="180" cy="200" r="8" fill="url(#grad)" />
                <path d="M90 70 C130 90, 210 120, 270 150 S330 200, 180 200"
                      fill="none" stroke="url(#grad)" strokeWidth="4" />
              </Svg>
            </Illustration>

            <div style={{ fontSize: 12, opacity: 0.65 }}>
              Podpowiedź: możesz wpisać np. <Mono>/zakupy/dane</Mono> i wcisnąć Enter.
            </div>
          </Box>
        </Grid>
      </Card>
    </Wrapper>
  );
}
